import {Vector2} from "../interfaces/Vector2";
const RAD2DEG = 57.29578;
const DEG2RAD = 0.017453292;
const EPSILON = 1.1754944e-38;

export const dist = ([x1, y1]: Vector2, [x2, y2]: Vector2) =>
    Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));

const lerp = (x: number, y: number, t: number) => x * t + y * (1 - t);
const v_lerp = ([x, y]: Vector2, [x2, y2]: Vector2, t: number): Vector2 => [
    lerp(x, x2, t),
    lerp(y, y2, t)
];
const v_len = ([x, y]: Vector2) => Math.sqrt(x * x + y * y);
const v_dot = ([lx, ly]: Vector2, [rx, ry]: Vector2): number => lx * rx + ly * ry;
const v_sum = ([x, y]: Vector2, [x2, y2]: Vector2): Vector2 => [x + x2, y + y2];
const v_sub = ([x, y]: Vector2, [x2, y2]: Vector2): Vector2 => [x - x2, y - y2];
const v_scale = (v: Vector2, s: number): Vector2 => v.map(v => v * s) as Vector2;
const v_rotate_around = ([cx, cy]: Vector2, [x, y]: Vector2, a: number): Vector2 => {
    const [px, py] = v_rotate([x, y], a);
    return [px + cx, py + cy];
};
const v_rotate = ([x, y]: Vector2, a: number): Vector2 => [
    x * Math.cos(a) - y * Math.sin(a),
    x * Math.sin(a) + y * Math.cos(a)
];
const v_norm = (v: Vector2): Vector2 => {
    const len = v_len(v),
        [x, y] = v;
    return [x / len, y / len];
};
const approximately = (a: float, b: float): bool =>
    Math.abs(b - a) < Math.max(1e-6 * Math.max(Math.abs(a), Math.abs(b)), EPSILON * 8);
const v_angle = ([x, y]: Vector2, [x2, y2]: Vector2) => Math.atan2(y2 - y, x2 - x);
const clamp = (value: number, min: number, max: number): number => {
    if (value < min) value = min;
    else if (value > max) value = max;
    return value;
};
const repeat = (t: number, length: number): number =>
    clamp(t - Math.floor(t / length) * length, 0, length);
// TODO: add description
const remap = (value: float | int, fromMin: float, fromMax: float, toMin: float, toMax: float) =>
    ((value - fromMin) * (toMax - toMin)) / (fromMax - fromMin) + toMin;

const round = (value: number, precision = 1000) => Math.round(value * precision) / precision;
const array_summ = (arr: number[]) => arr.reduce((sum, current) => sum + current, 0);
export const math = {
    lerp: v_lerp,
    len: v_len,
    sum: v_sum,
    sub: v_sub,
    scale: v_scale,
    rotate: v_rotate,
    rotateAround: v_rotate_around,
    norm: v_norm,
    dist,
    dot: v_dot,
    clamp,
    repeat,
    remap,
    approximately,
    angle: v_angle,
    round,
    array_summ,
    RAD2DEG,
    DEG2RAD,
    EPSILON
};
